import {
  Component,
  ViewChild,
  ElementRef,
  AfterContentInit,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  TemplateRef,
  OnDestroy,
  OnChanges,
  OnInit,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';

import { AppTemplateDirective } from 'app/directive/template.directive';
import { config } from 'app/constants';
import { DataService } from 'app/services/data.service';

import { debounce, trim } from 'lodash';

@Component({
  selector: 'app-auto-complete',
  templateUrl: './auto-complete.component.html',
  styleUrls: ['./auto-complete.component.scss'],
})
export class AutoCompleteComponent implements OnInit, AfterContentInit, OnDestroy, OnChanges {
  @Input() placeholder: string = '';
  @Input() name: string = '';
  @Input() value: string = '';
  @Input() suggestions: any[] = [];
  @Input() defaultSuggestions: any[] = [];
  @Input() field: string = 'name';
  @Input() editFieldLabel: string = 'Create New';
  @Input() editFieldPlaceholder: string = this.editFieldLabel;
  @Input() createButtonTitle: string = 'Save';
  @Input() leftIcon: string = '';
  @Input() fontSize: number = 12;
  @Input() listItemFontSize: number = this.fontSize;
  @Input() theme: string = '';
  @Input() scrollHeight: string = '120px';
  @Input() itemWidth: string = '';
  @Input() minSearchLength: number = 3;
  @Input() isSaveForFuture: boolean = false;
  @Input() closeOnSelection: boolean = true;
  @Input() openDropdownOnGetResult: boolean = false;
  @Input() delay: number = config.searchDelay;
  @Input() showSelectedValue: boolean = true;
  @Input() isDropdownIconVisible: boolean = false;
  @Input() isClearIconVisible: boolean = false;
  @Input() autoFocus: boolean = false;
  @Input() hasEditableField: boolean = false;
  @Input() hasCustomEditableField: boolean = false;
  @Input() hasCustomListTemplate: boolean = false;
  @Input() hasStaticUnassignedFiled: boolean = false;
  @Input() hasColorSelection: boolean = false;
  @Input() hasSearchIcon: boolean = false;
  @Input() hasError: boolean = false;
  @Input() hasHeaderButton: boolean = false;
  @Input() headerButtonTitle: boolean = false;
  @Input() disabled: boolean = false;
  @Input() isInvalid: boolean = false;
  @Input() readonly: boolean = false;
  @Input() hasHoverEffect: boolean = false;
  @Input() hasBgOnActive: boolean = false;
  @Input() hasBorderBottomOnActive: boolean = true;
  @Input() rightIcon: boolean = false;
  @Input() floatingLabel: boolean = false;
  @Input() hasFutureSelectionField: boolean = true;
  @Input() isSetPositionDynamically: boolean = false;
  @Input() vPosition: string = 'bottom'; // ['top', 'bottom]
  @Input() hPosition: string = ''; // ['left', 'right]
  @Input() label: string = this.placeholder;
  @Input() errorMessage: string = '';
  @Input() maxLength: string = '50';
  @Input() mask: string = '';
  @Input() prefix: string = '';
  @Input() showDisabledIcon: boolean = true;
  @Input() selectInactiveItems: boolean = true;
  @Input() allowMultiline: boolean = false;
  @Input() isTriggerChangeOnPaste: boolean = true;
  @Input() inactiveItemsKey: string = '';
  @Input() id: string = '';
  @Input() listId: string = '';
  @Input() textAreaId: string = '';
  @Input() maxAutoGrowHeight: number = 25;
  @Input() highlightFirstSelectedValue: boolean = false;
  @Input() highlightField: string = '';
  @Input() highlightFirstOption: boolean = false;
  @Input() selectionOnTab: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() isProgressiveLoading: boolean = false;
  @Input() selectTextOnFocus: boolean = false;
  @Input() clearQueryOnClose: boolean = false;
  @Input() hasCreateField: boolean = false;
  @Input() textTruncate: boolean = false;
  @Input() noRecordText: string = 'No result match';
  @Input() noRecordIcon: string = '';
  @Input() textColor: string = '';
  @Input() editableItemIdKey: string = '';
  @Input() staticListHeaderTitle: string = '';
  @Input() hasListHeader: boolean = false;
  @Input() isShowNoRecordForceFully: boolean = false;
  @Input() isShowError: boolean = false;
  @Input() hasStaticListHeader: boolean = false;
  @Input() isLoadingData: boolean = false;
  @Input() listHeaderTitle: string = '';
  @Input() hasListHeaderData: any;
  @Input() imgIcon: string = '';
  @Input() headerButtonIcon: string = '';

  @Input() styleClass: string = '';
  @Input() styleClassInput: string = '';
  @Input() styleClassInputWrapper: string = '';
  @Input() styleClassLabel: string = '';
  @Input() styleClassListWrapper: string = '';
  @Input() styleClassList: string = '';
  @Input() styleClassListItem: string = '';
  @Input() styleClassError: string = '';
  @Input() imgIconClass: string = '';
  @Input() headerButtonClass: string = '';
  @Input() confirmClearReq: boolean = false;

  @Output() appChange = new EventEmitter<string>();
  @Output() appFocus = new EventEmitter<any>();
  @Output() appBlur = new EventEmitter<any>();
  @Output() appSelect = new EventEmitter<any>();
  @Output() appCompletedMethod = new EventEmitter<any>();
  @Output() appSave = new EventEmitter<any>();
  @Output() appUpdateItem = new EventEmitter<any>();
  @Output() appClickOutside = new EventEmitter<any>();
  @Output() appClickHeader = new EventEmitter<any>();
  @Output() appClear = new EventEmitter<any>();
  @Output() appClickCreate = new EventEmitter<any>();
  @Output() appEnter = new EventEmitter<any>();
  @Output() appTabPress = new EventEmitter<any>();
  @Output() appEscPress = new EventEmitter<any>();
  @Output() appShiftTabPress = new EventEmitter<any>();
  @Output() appClickInput = new EventEmitter<any>();
  @Output() appDropdownIconClick = new EventEmitter<any>();
  @Output() scroll = new EventEmitter<void>();

  @ViewChild('autoCompleteInputControl', { static: true })
  autoCompleteInputControl: any;
  @ViewChild('autoCompleteContainer', { static: true })
  autoCompleteContainer: ElementRef;
  @ViewChild('createInput') createInput: ElementRef;
  @ViewChild('createField') createField: ElementRef;
  @ViewChild('autoCompleteListWrapper')
  autoCompleteListWrapper: ElementRef;
  @ViewChild('autoCompleteInputWrapper')
  autoCompleteInputWrapper: ElementRef;
  @ViewChild('autoCompletedList')
  autoCompletedList: ElementRef;
  @ViewChild('textareaControl')
  textareaControl: ElementRef;

  @ContentChildren(AppTemplateDirective) templates: QueryList<any>;

  autocompleteItem: TemplateRef<any>;
  autocompleteItemStatic: TemplateRef<any>;
  autocompleteItemEditable: TemplateRef<any>;
  autoCompletedListTemplate: TemplateRef<any>;
  isDropdownExpanded: boolean = false;
  isSearching: boolean = false;
  isEditable: boolean = false;
  editableItemId: string = '';
  query: string = '';
  selectedColor: string = config.colors[0].value;
  colors: any[] = config.colors;
  searchListener: any;
  isViewInitialized: boolean = false;
  clickOutsideListener: any;
  isAlreadyOpen: boolean = false;
  isActive: boolean = false;
  selectedOptionIndex: number = -1;
  noResultFound: object = {};
  completeMethod = debounce(this._completeMethod, this.delay);
  _trim = trim;

  constructor(
    private dataService: DataService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    if (!this.highlightField) {
      this.highlightField = this.field;
    }

    if (this.allowMultiline) {
      setTimeout(() => {
        try {
          const height = this.textareaControl.nativeElement.scrollHeight;

          if (height && height < this.maxAutoGrowHeight) {
            $(`#V${this.textAreaId}`).css('height', `${height}px`);
          } else if (height) {
            $(`#V${this.textAreaId}`).css('height', `${this.maxAutoGrowHeight}px`);
          }

          $(`#V${this.textAreaId}`).on('input', this.setTextAreaHeight);
        } catch (err) {}
      }, 1000);
    }
    if (this.defaultSuggestions && this.hasListHeaderData && this.hasListHeaderData.customer) {
      this.query = `${this.hasListHeaderData.customer.firstName} ${this.hasListHeaderData.customer.lastName}`;
    }

    this.completeMethod = debounce(this._completeMethod, this.delay);
  }

  ngAfterContentInit() {
    /** Focus AutoComplete Input When Initialized */
    setTimeout(() => {
      this.isViewInitialized = true;
      if (this.autoFocus) {
        this.autoCompleteInputControl.nativeElement.focus();
      }
    }, 100);

    this.getCustomTemplate();
    this.completeMethod('');
  }

  ngOnDestroy() {
    this.removeOutsideClickListener();
  }

  ngOnChanges(change) {
    const { autoFocus, value, suggestions } = change;

    if (autoFocus) {
      if (!autoFocus.previousValue && autoFocus.currentValue) {
        if (this.autoCompleteInputControl.nativeElement) {
          setTimeout(() => {
            this.autoCompleteInputControl.nativeElement.focus();
          }, 10);
        }
      }
    }

    if (value) {
      if ((!value.previousValue && value.currentValue) || value.previousValue !== value.currentValue) {
        setTimeout(() => {
          const height = this.textareaControl.nativeElement.scrollHeight;

          if (height && height < this.maxAutoGrowHeight) {
            $(`#V${this.textAreaId}`).css('height', `${height}px`);
          } else if (height) {
            $(`#V${this.textAreaId}`).css('height', `${this.maxAutoGrowHeight}px`);
          }
        }, 500);
      }
    }

    if (suggestions && suggestions.currentValue) {
      this.selectFirstMatchingItem();
    }
  }

  selectFirstMatchingItem() {
    if (this.suggestions) {
      if (this.suggestions && this.suggestions.length && this.highlightFirstOption) {
        this.selectedOptionIndex = 0;
        // this.setFocusOnSelectedValue();
      } else if (this.highlightFirstSelectedValue && this.query) {
        this.selectedOptionIndex = -1;

        for (let i = 0; i < this.suggestions.length; i++) {
          let suggestion = (this.suggestions[i] && this.suggestions[i][this.field]) || '';
          suggestion = suggestion.toLowerCase();

          if (suggestion.startsWith(this.query.toLowerCase()) && (this.selectInactiveItems || this.suggestions[i][this.inactiveItemsKey])) {
            this.selectedOptionIndex = i;
            this.setFocusOnSelectedValue();
            break;
          }
        }
      }
    }
  }

  setTextAreaHeight = (event: any) => {
    const self = event.target;

    if (self.scrollHeight > 25) {
      self.style.height = 'auto';
      self.style.height = self.scrollHeight + 'px';
      self.style.maxHeight = this.maxAutoGrowHeight + 'px';
    }
  };

  addclickOutsideListener() {
    this.removeOutsideClickListener();
    this.clickOutsideListener = this.dataService.getClickEvent().subscribe((target) => {
      this.listenClickOutside(target);
      if (!this.autoCompleteInputControl.nativeElement.contains(target)) {
        this.isActive = false;
      }
    });
  }

  removeOutsideClickListener() {
    if (this.clickOutsideListener && this.clickOutsideListener !== undefined) {
      this.clickOutsideListener.unsubscribe();
    }

    this.isAlreadyOpen = false;
  }

  listenClickOutside(target: any) {
    if (!this.isViewInitialized) {
      return;
    }

    /** Checking whether the click is outside the auto complete component or not (it will close the dropdown on click outside) */
    if (this.autoCompleteContainer.nativeElement.contains(target)) {
    } else {
      this.isDropdownExpanded = false;
      this.removeOutsideClickListener();
      this.appClickOutside.emit();

      if (this.clearQueryOnClose) {
        this.value = '';
        this.query = '';
        this.editableItemId = '';
      }

      if (this.autoCompleteInputControl.nativeElement.value !== this.value && this.value) {
        this.query = '';
        this.autoCompleteInputControl.nativeElement.value = this.value;
      }
      this.cdr.detectChanges();
    }

    /** Checking whether the click is outside the create new section or not (it will hide the input box on click outside)*/
    if (this.createField && this.createField.nativeElement && this.createField.nativeElement.contains(target)) {
    } else {
      this.isEditable = false;
    }
  }

  handleClose = () => {
    this.isDropdownExpanded = false;
    this.removeOutsideClickListener();
    this.appClickOutside.emit();
  };

  onPaste(event: ClipboardEvent) {
    if (!this.isTriggerChangeOnPaste) return;
    let clipboardData = event.clipboardData || (<any>window).clipboardData;
    let pastedText = clipboardData.getData('text');
    if (pastedText) {
      this.onChange({
        target: {
          value: pastedText,
        },
        key: 'Pasted',
      });
    }
  }

  onChange(event: any) {
    const { value } = event.target;

    if (event.key === 'Enter' || event.key === 'Escape') {
      return;
    }

    this.appChange.emit(value);

    if (this.query !== value) {
      this.query = value;
      this.completeMethod(this.query);
    }

    if (!value) {
      this.appClear.emit();
      this.isDropdownExpanded = true;
      this.query = '';
    }
  }

  onChangeType(event: any) {
    const { value } = event.target;
    if (value === '') {
      this.isShowError = false;
    }
    this.appChange.emit(value);
  }

  onEnter() {
    if (this.selectedOptionIndex >= 0) {
      const option = this.suggestions[this.selectedOptionIndex];

      if (option) {
        this.onSelect(option);
      }
    } else {
      this.removeOutsideClickListener();
      this.isDropdownExpanded = false;
    }
    if (this.suggestions && this.suggestions.length) {
      this.appEnter.emit(this.query);
    }
  }

  onTabPress() {
    if (this.selectionOnTab && this.selectedOptionIndex >= 0) {
      this.onEnter();
    }

    this.isDropdownExpanded = false;
    this.removeOutsideClickListener();
    this.appTabPress.emit(this.query);
  }

  /** select color for the created option */
  onColorSelect(value) {
    this.selectedColor = value;
  }

  /** emit on selection of the item (it will not emit on static and create new item) */
  onSelect(option: any) {
    if (!this.selectInactiveItems && this.inactiveItemsKey && !option[this.inactiveItemsKey]) {
      return;
    }

    if (!this.showSelectedValue) {
      this.autoCompleteInputControl.nativeElement.value = '';
    } else {
      this.autoCompleteInputControl.nativeElement.value = this.value;
    }

    this.appSelect.emit(option);
    if (this.closeOnSelection) {
      this.isDropdownExpanded = false;
      // if (this.isClearIconVisible) {
      this.removeOutsideClickListener();
      // }
    }

    if (this.allowMultiline) {
      setTimeout(() => {
        try {
          const height = this.textareaControl.nativeElement.scrollHeight;

          if (height && height < this.maxAutoGrowHeight) {
            $(`#V${this.textAreaId}`).css('height', `${height}px`);
          } else if (height) {
            $(`#V${this.textAreaId}`).css('height', `${this.maxAutoGrowHeight}px`);
          }
        } catch (err) {}
      }, 1000);
    }
  }

  /** emit on focusing the autocomplete box (it will open the suggestions dropdown) */
  onFocus(emitInputClick = false) {
    if (this.disabled) {
      return;
    }

    this.isActive = true;
    this.appFocus.emit();
    this.autoCompleteInputControl.nativeElement.focus();

    if (this.selectTextOnFocus) {
      this.autoCompleteInputControl.nativeElement.select();
    }

    this.isDropdownExpanded = true;

    if (!this.isAlreadyOpen) {
      this.setDropdownPosition();
    }

    this.isAlreadyOpen = true;
    this.addclickOutsideListener();

    if (emitInputClick) {
      this.appClickInput.emit();
    }
  }

  onDropdownIconClick() {
    this.appDropdownIconClick.emit();
  }

  /** emit on the blur event of the input box */
  onBlur() {
    this.isAlreadyOpen = false;
    this.isActive = this.isClearIconVisible ? true : false;
    this.appBlur.emit();
  }

  _completeMethod(query: string) {
    if (query && query !== undefined && query.length >= this.minSearchLength) {
      this.appCompletedMethod.emit(query);
      if (this.openDropdownOnGetResult) {
        this.isDropdownExpanded = true;
      }

      if (this.highlightFirstSelectedValue) {
        this.selectFirstMatchingItem();
      }
    }
  }

  /** emit when user click on the save button on the create new section (it will only emit if the query >= 3) */
  onCreate() {
    if (this.editableItemId) {
      this.appUpdateItem.emit({
        _id: this.editableItemId,
        name: this.query,
        color: this.selectedColor,
        isForFurtureUse: this.isSaveForFuture,
      });
    } else {
      this.autoCompleteInputControl.nativeElement.value = this.query;
      this.appSave.emit({
        name: this.query,
        color: this.selectedColor,
        isForFurtureUse: this.isSaveForFuture,
      });
    }

    if (!this.showSelectedValue) {
      this.autoCompleteInputControl.nativeElement.value = '';
    } else if (!this.editableItemId) {
      this.appSelect.emit({ name: this.query });
    }
    this.selectedColor = config.colors[0].value;
    this.query = '';
    this.toggleEditMode();

    if (!this.editableItemId) {
      this.isDropdownExpanded = false;
      this.removeOutsideClickListener();
    }

    this.editableItemId = '';
  }

  /** emit when user click on the autocomplete input (it will open the suggestion dropdown if it's not open) */
  onInputFieldClick() {
    this.appClickInput.emit();
  }

  onFutureSaveClick(isSaveForFuture) {
    this.isSaveForFuture = isSaveForFuture;
  }

  onCloseFromOutside = (removeSearchQuery: boolean = true) => {
    this.isDropdownExpanded = false;

    if (removeSearchQuery) {
      this.value = '';
      this.autoCompleteInputControl.nativeElement.value = '';
    }
    this.removeOutsideClickListener();
  };

  onEscPress(event: Event, type: string = '') {
    if (type === 'shift-tab') {
      this.appShiftTabPress.emit(event);
    }

    this.isDropdownExpanded = false;
    this.removeOutsideClickListener();
    this.appEscPress.emit();
  }

  onSelectOption(type: string) {
    if (type === 'up') {
      if (this.selectedOptionIndex === 0) {
        return;
      }

      this.selectedOptionIndex--;
    } else {
      if (this.suggestions && this.selectedOptionIndex === this.suggestions.length - 1) {
        this.selectedOptionIndex = 0;
      } else {
        this.selectedOptionIndex++;
      }
    }

    if (this.suggestions && this.selectedOptionIndex - 1 > this.suggestions.length) {
      this.selectedOptionIndex = 0;
    }

    this.setFocusOnSelectedValue(type);
  }

  setFocusOnSelectedValue(type?: string) {
    setTimeout(() => {
      const selectedEl = this.autoCompletedList.nativeElement.querySelector(`#item-${this.id}${this.selectedOptionIndex}`) as any;

      if (selectedEl) {
        if (selectedEl && selectedEl.dataset && selectedEl.dataset.parent) {
          const listContainer = this.autoCompletedList.nativeElement.querySelector(`#${selectedEl.dataset.container}`);

          if (listContainer) {
            const listParent = this.autoCompletedList.nativeElement.querySelector(`#${selectedEl.dataset.parent}`);

            const listSibling = this.autoCompletedList.nativeElement.querySelector(`#${selectedEl.dataset.sibling}`);

            if (listParent) {
              let scrollHeight = Number(selectedEl.dataset.scrollHeight);
              let extraOffSetTop = Number(selectedEl.dataset.extraScrollOffset || 1);

              if (listSibling) {
                scrollHeight = listParent.offsetHeight + listSibling.offsetHeight + 74;
              }

              if (listSibling) {
                extraOffSetTop = listSibling.offsetHeight + 74;
              }

              if (selectedEl.offsetTop >= scrollHeight || selectedEl.offsetTop === extraOffSetTop) {
                listParent.scrollTop = selectedEl.offsetTop - Number(scrollHeight) + selectedEl.offsetHeight;
                this.autoCompletedList.nativeElement.scrollTop = selectedEl.offsetTop - Number(scrollHeight) + selectedEl.offsetHeight;
              }
            }
          }
        } else {
          const scrollHeight = parseInt(this.scrollHeight).toString();
          if (selectedEl.offsetTop >= scrollHeight || selectedEl.offsetTop == 1) {
            selectedEl.parentNode.scrollTop = selectedEl.offsetTop - Number(scrollHeight) + selectedEl.offsetHeight;
            this.autoCompletedList.nativeElement.scrollTop = selectedEl.offsetTop - Number(scrollHeight) + selectedEl.offsetHeight;
          }
        }
      }
    }, 10);
  }

  /** It will show the specific field in the dropdown given in the field input */
  resolveFieldData(option) {
    return option[this.field] || '';
  }

  /** It will toggle the edit mode of the create new input */
  toggleEditMode() {
    this.isEditable = !this.isEditable;
    if (this.isEditable) {
      this.appClickCreate.emit(this.isEditable);
      if (!this.value) {
        this.query = this.autoCompleteInputControl.nativeElement.value;
      } else {
        this.query = '';
      }
    }
    if (this.isEditable) {
      setTimeout(() => {
        if (this.createInput && this.createInput.nativeElement) {
          this.createInput.nativeElement.focus();
        }
      }, 10);
    }
  }

  /** emit click event of the header button  */
  handleHeaderBtnClick() {
    this.appClickHeader.emit(this.value);
    if (this.clearQueryOnClose) {
      this.value = '';
      this.query = '';
    }
  }

  /** get all the custom templates */
  getCustomTemplate() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'item':
          this.autocompleteItem = item.template;
          break;

        case 'static':
          this.autocompleteItemStatic = item.template;
          break;

        case 'list':
          this.autoCompletedListTemplate = item.template;
          break;

        case 'editable':
          this.autocompleteItemEditable = item.template;
          break;
      }
    });
  }

  onCancelEdit() {
    if (!this.confirmClearReq) {
      this.isEditable = false;
      this.value = '';
      this.query = '';
      this.editableItemId = '';
    }
    this.appClear.emit();
  }

  closeSuggestions = () => {
    this.isDropdownExpanded = false;
    this.removeOutsideClickListener();
  };

  clearQueries = () => {
    this.autoCompleteInputControl.nativeElement.value = '';
    this.query = '';
  };

  editItem = (item: any) => {
    this.isEditable = true;
    this.editableItemId = item[this.editableItemIdKey];

    if (this.isEditable) {
      this.appClickCreate.emit(this.isEditable);
      this.query = item.name || item[this.field];
      this.selectedColor = item.color;
      // this.autoCompleteInputControl.nativeElement.value = item.name;
    }

    if (this.isEditable) {
      setTimeout(() => {
        if (this.createInput && this.createInput.nativeElement) {
          this.createInput.nativeElement.focus();
        }
      }, 10);
    }
  };

  setDropdownPosition() {
    if (this.isSetPositionDynamically) {
      setTimeout(() => {
        /** Calculating Vertical Position of the dropdown */
        const dropdownListOffset = this.autoCompleteListWrapper.nativeElement.getBoundingClientRect();
        const dropdownListHeight = this.autoCompleteListWrapper.nativeElement.offsetHeight;
        const selectedViewHeight = this.autoCompleteInputWrapper.nativeElement.offsetHeight;
        const spaceUp = dropdownListOffset.top - selectedViewHeight - dropdownListHeight - $(window).scrollTop();
        const spaceDown = $(window).scrollTop() + $(window).height() - (dropdownListOffset.top + dropdownListHeight) - 20;

        if (spaceDown < 0 && (spaceUp >= 0 || spaceUp > spaceDown)) {
          this.vPosition = 'bottom';
        } else {
          this.vPosition = 'top';
        }
      }, 100);
    }
  }

  // @HostListener('scroll', ['$event'])
  // onScroll(event: any) {
  //   const element = event?.target;
  //     if (element?.offsetHeight + element?.scrollTop >= element?.scrollHeight - 1) {
  //     this.scroll.emit();
  //   }
  // }

  onScroll = debounce((event: any) => {
    if (this.isProgressiveLoading && this.isLoading) return;

    if (event.target?.offsetHeight + event.target?.scrollTop >= event.target?.scrollHeight - 1) {
      this.scroll.emit();
    }
  }, 300);
}
